import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { IconButton, InputAdornment } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import PauseCircleFilledIcon from '@mui/icons-material/PauseCircleFilled';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DateTime, Duration } from 'luxon';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useFormContext, useWatch } from 'react-hook-form';
import { useNoteContext } from 'modules/notes/NoteContext';
import { SimpleDialog } from 'modules/common';
import { TimeDurationControl } from 'modules/common/components/TimeDurationControl';
import { useTimesheetLabelSuggestionContext } from 'modules/timesheet-labels';
import { NOTE_FIELDS } from 'modules/notes/common';
import { useTimer, UseTimerResult } from './useTimer';
import { useTimerContext } from './TimerContext';

const messages = {
  title: <FormattedMessage id="note.resetStopTimerDialog.title" />,
  resetStopTimerDialog: [
    <FormattedMessage
      key="note.resetStopTimerDialog.message1"
      id="note.resetStopTimerDialog.message1"
    />,
    <FormattedMessage
      key="note.resetStopTimerDialog.message2"
      id="note.resetStopTimerDialog.message2"
    />
  ],
  resetTimer: <FormattedMessage id="note.resetStopTimerDialog.yes" />,
  cancel: <FormattedMessage id="note.resetStopTimerDialog.cancel" />
};

const usePlayStyles = makeStyles((theme) => ({
  root: {
    color: '#624CF2',
    padding: theme.spacing(0)
  }
}));

const useAdornmentStyles = makeStyles((theme) => ({
  positionEnd: {
    marginLeft: theme.spacing(0)
  }
}));

const usePauseStyles = makeStyles((theme) => ({
  root: {
    color: '#E83033',
    padding: theme.spacing(0)
  }
}));

const useResetStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
    visibility: ({ running }: { running: boolean }) =>
      running ? 'hidden' : 'visible'
  }
}));

const useInputStyles = makeStyles((theme) => ({
  root: {
    maxWidth: theme.spacing(22.4),
    padding: theme.spacing(0, 1.75, 0, 0)
  },
  disabled: {
    color: theme.palette.grey[900],
    '-webkit-text-fill-color': `${theme.palette.grey[900]} !important`
  }
}));

type TimerProps = {
  duration?: number;
  showStartEndTime?: boolean;
  resetTimeRange: () => void;
  resetDuration: () => void;
  handleDuration(duration: Duration | null, showStartEndTime?: boolean): void;
  className?: string;
  testId?: string;
  resetTimeValue?: boolean | undefined;
  setResetTimeValue?: React.Dispatch<React.SetStateAction<boolean | undefined>>;

  suggestionProps: {
    setHasSuggestionStartTime: React.Dispatch<React.SetStateAction<boolean>>;

    setHasSuggestionDuration: React.Dispatch<React.SetStateAction<boolean>>;

    setHasSuggestionEndTime: React.Dispatch<React.SetStateAction<boolean>>;

    hasSuggestionDuration: boolean;
  };
};

const useInputProps = ({
  running,
  onPlay,
  onPause,
  handleReset,
  handleBlur,
  disableTimer,
  hideTimer = false
}: {
  running: boolean;
  onPlay: UseTimerResult['onPlay'];
  onPause: UseTimerResult['onPause'];
  handleReset: () => void;
  handleBlur: () => void;
  disableTimer: boolean;
  hideTimer?: boolean;
}) => {
  const playClasses = usePlayStyles();
  const pauseClasses = usePauseStyles();
  const resetClasses = useResetStyles({ running });
  const classes = useInputStyles();
  const adormentClasses = useAdornmentStyles();
  const { date } = useNoteContext();
  const isToday = DateTime.local().toISODate() == date.toISODate();
  const { formatMessage } = useIntl();

  const playBtn = useMemo(
    () => (
      <InputAdornment position="end" classes={adormentClasses}>
        <IconButton
          onClick={onPlay}
          disabled={disableTimer}
          classes={playClasses}
          aria-label={formatMessage({ id: 'note.play' })}
          data-testid="timer-play-btn"
        >
          <PlayCircleFilledWhiteIcon
            color={disableTimer ? 'disabled' : 'primary'}
          />
        </IconButton>
      </InputAdornment>
    ),
    [adormentClasses, onPlay, disableTimer, playClasses, formatMessage]
  );

  const pauseBtn = useMemo(
    () => (
      <InputAdornment position="end" classes={adormentClasses}>
        <IconButton
          onClick={onPause}
          classes={pauseClasses}
          aria-label={formatMessage({ id: 'note.pause' })}
          data-testid="timer-pause-btn"
        >
          <PauseCircleFilledIcon />
        </IconButton>
      </InputAdornment>
    ),
    [adormentClasses, onPause, pauseClasses, formatMessage]
  );

  const resetBtn = useMemo(
    () => (
      <InputAdornment position="start">
        <IconButton
          onClick={handleReset}
          disabled={running}
          classes={resetClasses}
          aria-label={formatMessage({ id: 'note.reset' })}
          data-testid="timer-reset-btn"
          onBlur={handleBlur}
        >
          <RefreshIcon />
        </IconButton>
      </InputAdornment>
    ),
    [handleReset, running, resetClasses, formatMessage, handleBlur]
  );

  const endAdornment = useMemo(() => {
    const adornment = running ? pauseBtn : playBtn;
    const isHideTimer = !isToday || hideTimer;
    return !isHideTimer ? adornment : undefined;
  }, [pauseBtn, playBtn, running, isToday, hideTimer]);

  return useMemo(
    () => ({
      startAdornment: resetBtn,
      endAdornment: endAdornment,
      classes
    }),
    [classes, endAdornment, resetBtn]
  );
};
const durationLabel = <FormattedMessage id="note.duration" />;

export const Timer: React.FC<TimerProps> = ({
  duration: initialDuration,
  resetTimeRange,
  resetDuration,
  handleDuration,
  className,
  showStartEndTime,
  testId,
  setResetTimeValue,
  suggestionProps,
  ...rest
}: TimerProps) => {
  const [durationField, setDurationField] = useState<number>(
    initialDuration ?? 0
  );
  const {
    running,
    timer,
    onPlay,
    onPause,
    onReset,
    onChange,
    today
  } = useTimer({
    duration: durationField,
    resetTimeRange,
    resetDuration,
    handleDuration,
    showStartEndTime,
    setResetTimeValue,
    suggestionProps
  });

  const { disableTimer, hideTimer } = useTimerContext();

  const [
    resetClickedOnInvalidDuration,
    setResetClickedOnInvalidDuration
  ] = useState<boolean>(false);

  const handleBlur = useCallback(() => {
    setResetClickedOnInvalidDuration(false);
  }, []);

  const handleReset = useCallback(() => {
    setResetClickedOnInvalidDuration(disableTimer);
    onReset();
    setDurationField(0);
  }, [disableTimer, onReset]);

  const inputProps = useInputProps({
    running,
    onPlay,
    onPause,
    handleReset,
    handleBlur,
    disableTimer,
    hideTimer
  });

  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const { setValue, control } = useFormContext();
  const { date } = useNoteContext();
  const isToday = DateTime.local().toISODate() === date.toISODate();
  const duration = useWatch({ name: NOTE_FIELDS.DURATION, control });
  const { labelSuggestions } = useTimesheetLabelSuggestionContext();

  useEffect(() => {
    if (duration && !suggestionProps.hasSuggestionDuration) {
      setDurationField(duration);
    } else if (
      labelSuggestions?.labelSuggestions.duration &&
      suggestionProps.hasSuggestionDuration
    ) {
      setDurationField(labelSuggestions?.labelSuggestions.duration);
    } else {
      setDurationField(duration);
    }
  }, [
    suggestionProps.hasSuggestionDuration,
    duration,
    labelSuggestions?.labelSuggestions.duration
  ]);

  useEffect(() => {
    if (running && !isToday && !(DateTime.local() >= today.current)) {
      setDialogIsOpen(true);
    }
  }, [running, isToday, today]);

  const closeDialog = useCallback(() => {
    setValue(NOTE_FIELDS.DATE, DateTime.local().toJSDate(), {
      shouldDirty: true
    });
    setDialogIsOpen(false);
  }, [setValue]);

  const resetTimer = useCallback(() => {
    onPause();
    onReset();
    setDialogIsOpen(false);
  }, [onPause, onReset]);

  return (
    <>
      <SimpleDialog
        open={dialogIsOpen}
        onClose={closeDialog}
        onPrimaryAction={closeDialog}
        onSecondaryAction={resetTimer}
        title={messages.title}
        messages={messages.resetStopTimerDialog}
        primaryActionButtonTitle={messages.cancel}
        secondaryActionButtonTitle={messages.resetTimer}
        closeOnBlur={false}
      />

      <TimeDurationControl
        timer={timer}
        testId={testId || 'add-note-duration'}
        label={durationLabel}
        className={classNames(className)}
        disabled={running}
        onEnter={onChange}
        running={running}
        duration={durationField}
        InputProps={inputProps}
        resetClickedOnInvalidDuration={resetClickedOnInvalidDuration}
        size="small"
        {...rest}
      />
    </>
  );
};
